import React, { useState, useRef, useEffect } from "react";
import useEffectOnce from "../../useEffectOnce";
import "./Publisher.scss";
import { Radio, RadioGroup, FormControlLabel, Tooltip, Snackbar, Alert } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LogicModal from "../../Components/LogicModal/LogicModal";
import AiConfidenceModal from "../../Components/AiConfidenceModal/AiConfidenceModal";
import { serverAddr } from "../../networkConfig";
import SearchCoin from "../../Components/SearchCoin/SearchCoin";


const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;
    const formatted = date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
    return `${formatted} IST`;
  } catch {
    return timestamp;
  }
};

// Target limits based on active entry (upper for long, lower for short)
const calculateTargetLimits = (activeEntry, opinion, entryRange1, stoploss, entryRange2) => {
  const e = Number(activeEntry);
  if (!e) return { t1Limit: null, t2Limit: null };

  const minRisk = e * 0.003;

  if (opinion === "long") {
    const er2 = Number(entryRange2);
    const sl = Number(stoploss);
    if (er2 && sl) {
      const slPctDecimal = (er2 - sl) / er2;
      const t1Min = er2 * (1 + Math.max(slPctDecimal * 1.01, 0.015));
      const t2Min = er2 * (1 + Math.max(slPctDecimal * 1.51, 0.025));
      return {
        t1Limit: parseFloat(t1Min.toFixed(4)),
        t2Limit: parseFloat(t2Min.toFixed(4))
      };
    }
    const t1Min = Math.max(e + minRisk, e * 1.015);
    const t2Min = Math.max(e + minRisk * 1.5, e * 1.025);
    return {
      t1Limit: parseFloat(t1Min.toFixed(4)),
      t2Limit: parseFloat(t2Min.toFixed(4))
    };
  } else {
    const er1 = Number(entryRange1);
    const sl = Number(stoploss);
    if (er1 && sl) {
      const slPctDecimal = (sl - er1) / er1;
      const t1Max = er1 * (1 - Math.max(slPctDecimal * 1.01, 0.015));
      const t2Max = er1 * (1 - Math.max(slPctDecimal * 1.51, 0.025));
      return {
        t1Limit: parseFloat(t1Max.toFixed(4)),
        t2Limit: parseFloat(t2Max.toFixed(4))
      };
    }
    const t1Max = e - Math.max(minRisk, e * 0.015);
    const t2Max = e - Math.max(minRisk * 1.5, e * 0.025);
    return {
      t1Limit: parseFloat(t1Max.toFixed(4)),
      t2Limit: parseFloat(t2Max.toFixed(4))
    };
  }
};

// Stoploss range based on active entry (upper for long, lower for short)
//   slClose = activeEntry ± 0.3%
//   slFar   = activeEntry * (1 ± 0.9/Lev)
const calculateStoplossRange = (activeEntry, opinion, leverage) => {
  if (!activeEntry) return { min: "", max: "" };
  const e = Number(activeEntry);
  const lev = Number(leverage);

  const slClose = opinion === "long"
    ? parseFloat((e * 0.997).toFixed(4))   // 0.3% below active entry
    : parseFloat((e * 1.003).toFixed(4));  // 0.3% above active entry

  if (!lev) {
    return opinion === "long"
      ? { min: "", max: slClose }
      : { min: slClose, max: "" };
  }

  const factor = Math.min(0.9 / lev, 20 / lev);
  const slFar = opinion === "long"
    ? parseFloat((e * (1 - factor)).toFixed(4))
    : parseFloat((e * (1 + factor)).toFixed(4));

  return opinion === "long"
    ? { min: slFar, max: slClose }
    : { min: slClose, max: slFar };
};

const numericOnly = (value) => value.replace(/[^0-9.]/g, "");

const getTodayStr = () => new Date().toISOString().split('T')[0];
const getMaxDateStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 90);
  return d.toISOString().split('T')[0];
};
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, day] = dateStr.split('-');
  return `${day}/${m}/${y}`;
};

function Card({ coin, setCoins, index }) {
  const [opinion, setOpinion] = useState("long");
  const [entryType, setEntryType] = useState("limit");
  const [markPrice, setMarkPrice] = useState(coin.markPrice ? parseFloat(Number(coin.markPrice).toFixed(4)) : "");

  // entryRange2 = upper, entryRange1 = lower
  // Long:  upper is primary (user types), lower is auto-calc (user can edit)
  // Short: lower is primary (user types), upper is auto-calc (user can edit)
  const [entryRange1, setEntryRange1] = useState(""); // lower
  const [entryRange2, setEntryRange2] = useState(""); // upper

  const [target1, setTarget1] = useState("");
  const [target2, setTarget2] = useState("");
  const [stoploss, setStoploss] = useState("");
  const [leverage, setLeverage] = useState("");
  const [validity, setValidity] = useState("");
  const [openLogic, setOpenLogic] = useState(false);
  const [openAiScore, setOpenAiScore] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPublished, setIsPublished] = useState(false);
  const [targetTouched, setTargetTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const dateInputRef = useRef(null);

  // Long → active entry is upper (entryRange2)
  // Short → active entry is lower (entryRange1)
  const activeEntry = opinion === "long" ? Number(entryRange2) : Number(entryRange1);

  // Midpoint sent to backend as entry_price (for Zebpay recommendations)
  const entryMidpoint = (Number(entryRange1) && Number(entryRange2))
    ? (Number(entryRange1) + Number(entryRange2)) / 2
    : 0;

  const { t1Limit, t2Limit } = calculateTargetLimits(activeEntry, opinion, entryRange1, stoploss, entryRange2);
  const { min: slRangeMin, max: slRangeMax } = calculateStoplossRange(activeEntry, opinion, Number(leverage));

  // Reset entry ranges when opinion changes
  useEffect(() => {
    setEntryRange1("");
    setEntryRange2("");
  }, [opinion]);

  const calculateRR = (activeEntry, stoploss, target1, opinion) => {
    const risk = Math.abs(activeEntry - stoploss);
    if (risk === 0) return 0;
    if (opinion === "long") return (target1 - activeEntry) / risk;
    return (activeEntry - target1) / risk;
  };

  const validateStoploss = (activeEntry, stoploss, opinion, leverage) => {
    const { min, max } = calculateStoplossRange(activeEntry, opinion, leverage);
    if (!min && !max) return true;
    if (!min) return stoploss <= max;
    if (!max) return stoploss >= min;
    return stoploss >= min && stoploss <= max;
  };

  const validateAll = ({
    markPrice,
    stoploss,
    target1,
    target2,
    opinion,
    entryRange1,
    entryRange2,
    leverage,
    backendLeverage
  }) => {
    const errors = {};
    const er1 = Number(entryRange1);
    const er2 = Number(entryRange2);
    const ae = opinion === "long" ? er2 : er1;

    // LEVERAGE VALIDATION
    const lev = Number(leverage);
    if (leverage && (isNaN(lev) || lev < 3)) {
      errors.leverage = "Leverage must be at least 3x";
    } else if (leverage && backendLeverage && lev > Number(backendLeverage)) {
      errors.leverage = `Leverage cannot exceed ${backendLeverage}x (exchange limit)`;
    }

    if (!ae || !stoploss || !target1 || !target2) {
      errors.general = "All fields are required";
      return { errors, final: "REJECTED" };
    }

    // PRIMARY ENTRY VALIDATION (±10% of mark price)
    const minEntry = markPrice * 0.9;
    const maxEntry = markPrice * 1.1;
    if (ae < minEntry || ae > maxEntry) {
      errors.primaryEntry = `Must be within ±10% of mark price (${minEntry.toFixed(2)} – ${maxEntry.toFixed(2)})`;
    }

    // STOPLOSS VALIDATION
    const { min: slMin, max: slMax } = calculateStoplossRange(ae, opinion, leverage);
    if (opinion === "long" && stoploss >= ae) {
      errors.stoploss = `Stoploss must be below upper entry. Valid range: ${slMin} – ${slMax}`;
    } else if (opinion === "short" && stoploss <= ae) {
      errors.stoploss = `Stoploss must be above lower entry. Valid range: ${slMin} – ${slMax}`;
    } else if ((slMin !== "" && stoploss < slMin) || (slMax !== "" && stoploss > slMax)) {
      errors.stoploss = `Stoploss must be between ${slMin} – ${slMax}`;
    }

    const rr = calculateRR(ae, stoploss, target1, opinion);

    // ENTRY RANGE VALIDATION
    if (er1 && er2) {
      if (er1 >= er2) {
        errors.entryRange = "Lower entry must be less than upper entry";
      } else if (er1 < minEntry || er1 > maxEntry || er2 < minEntry || er2 > maxEntry) {
        errors.entryRange = "Both entries must be within ±10% of mark price";
      } else if (target1 && stoploss) {
        // Entry range width must be >= 5% of |stoploss - target1|
        const rangeWidth = er2 - er1;
        const minWidth = 0.05 * Math.abs(Number(stoploss) - Number(target1));
        if (rangeWidth < minWidth) {
          errors.entryRange = `Entry range too narrow. Width must be ≥ ${parseFloat(minWidth.toFixed(4))} (5% of |stoploss − target1|)`;
        }
      }
    }

    // TARGET DIRECTION VALIDATION
    if (opinion === "long") {
      if (target1 <= ae) errors.target1 = "Target 1 must be above upper entry (LONG)";
      if (target2 <= target1) errors.target2 = "Target 2 must be above Target 1 (LONG)";
    } else {
      if (target1 >= ae) errors.target1 = "Target 1 must be below lower entry (SHORT)";
      if (target2 >= target1) errors.target2 = "Target 2 must be below Target 1 (SHORT)";
    }

    // TARGET RR VALIDATION
    const { t1Limit: t1Lim, t2Limit: t2Lim } = calculateTargetLimits(ae, opinion, entryRange1, stoploss, entryRange2);
    if (t1Lim !== null && !errors.target1) {
      if (opinion === "long" && target1 < t1Lim) errors.target1 = `Target 1 must be ≥ ${t1Lim} (min RR 1:1 or 1.5%)`;
      if (opinion === "short" && target1 > t1Lim) errors.target1 = `Target 1 must be ≤ ${t1Lim} (min RR 1:1 or 1.5%)`;
    }
    if (t2Lim !== null && !errors.target2) {
      if (opinion === "long" && target2 < t2Lim) errors.target2 = `Target 2 must be ≥ ${t2Lim} (min RR 1:1.5 or 2.5%)`;
      if (opinion === "short" && target2 > t2Lim) errors.target2 = `Target 2 must be ≤ ${t2Lim} (min RR 1:1.5 or 2.5%)`;
    }

    return {
      errors,
      final: Object.keys(errors).length === 0 ? "APPROVED" : "REJECTED",
      rr
    };
  };

  useEffect(() => {
    const ae = activeEntry;
    const mp = Number(markPrice);
    const sl = Number(stoploss);
    const t1 = Number(target1);
    const t2 = Number(target2);
    const lev = Number(leverage);

    if (!ae && !sl && !t1 && !t2) {
      setErrors({});
      return;
    }

    const newErrors = {};

    if (ae && mp) {
      const minEntry = parseFloat((mp * 0.9).toFixed(2));
      const maxEntry = parseFloat((mp * 1.1).toFixed(2));
      if (ae < minEntry || ae > maxEntry) {
        newErrors.primaryEntry = `Must be within ±10% of mark price (${minEntry} – ${maxEntry})`;
      }
    }

    // Validate entry range instantly as user types
    const er1 = Number(entryRange1);
    const er2 = Number(entryRange2);
    if (er1 && er2) {
      if (er1 >= er2) {
        newErrors.entryRange = "Lower entry must be less than upper entry";
      } else if (mp) {
        const minEntry = parseFloat((mp * 0.9).toFixed(2));
        const maxEntry = parseFloat((mp * 1.1).toFixed(2));
        if (er1 < minEntry || er1 > maxEntry || er2 < minEntry || er2 > maxEntry) {
          newErrors.entryRange = "Both entries must be within ±10% of mark price";
        }
      }
    }

    if (ae && sl && !validateStoploss(ae, sl, opinion, lev)) {
      const { min: slMin, max: slMax } = calculateStoplossRange(ae, opinion, lev);
      const rangeStr = slMin !== "" && slMax !== "" ? `${slMin} – ${slMax}` : slMin !== "" ? `≥ ${slMin}` : `≤ ${slMax}`;
      newErrors.stoploss = `Stoploss must be ${rangeStr}`;
    }

    if (ae && sl && t1) {
      // Validate target1 as soon as it's entered (don't wait for target2)
      const { t1Limit } = calculateTargetLimits(ae, opinion, entryRange1, sl, entryRange2);
      if (opinion === "long" && t1 <= ae) {
        newErrors.target1 = "Target 1 must be above upper entry (LONG)";
      } else if (opinion === "short" && t1 >= ae) {
        newErrors.target1 = "Target 1 must be below lower entry (SHORT)";
      } else if (t1Limit !== null) {
        if (opinion === "long" && t1 < t1Limit) newErrors.target1 = `Target 1 must be ≥ ${t1Limit} (min RR 1:1 or 1.5%)`;
        if (opinion === "short" && t1 > t1Limit) newErrors.target1 = `Target 1 must be ≤ ${t1Limit} (min RR 1:1 or 1.5%)`;
      }
    }

    if (ae && sl && t1 && t2) {
      const result = validateAll({
        markPrice: mp,
        stoploss: sl,
        target1: t1,
        target2: t2,
        entryRange1,
        entryRange2,
        opinion,
        leverage: lev,
        backendLeverage: coin.backendLeverage
      });
      const { general, ...fieldErrors } = result.errors;
      setErrors(fieldErrors);
    } else {
      setErrors(newErrors);
    }
  }, [stoploss, target1, target2, opinion, leverage, entryRange1, entryRange2, markPrice]);

  const handleLogicClick = () => {
    const ae = activeEntry;
    const mp = Number(markPrice);
    const sl = Number(stoploss);
    const t1 = Number(target1);
    const t2 = Number(target2);
    const lev = Number(leverage);
    const er1 = Number(entryRange1);
    const er2 = Number(entryRange2);

    if (!ae || !er1 || !er2 || !sl || !t1 || !t2 || !lev || !validity) {
      setErrors({
        general: "All fields are required",
        ...(!validity && { validity: "Validity date is required" })
      });
      return;
    }

    const result = validateAll({
      markPrice: mp,
      stoploss: sl,
      target1: t1,
      target2: t2,
      entryRange1,
      entryRange2,
      opinion,
      leverage: lev,
      backendLeverage: coin.backendLeverage
    });

    if (result.final === "REJECTED") {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setOpenLogic(true);
  };

  // % suffix — always relative to active entry
  // Long: stoploss% = (upperEntry - stoploss) / upperEntry * 100
  // Short: stoploss% = (stoploss - lowerEntry) / lowerEntry * 100
  const slPct = stoploss && activeEntry
    ? opinion === "long"
      ? `${parseFloat(((activeEntry - Number(stoploss)) / activeEntry * 100).toFixed(4))}%`
      : `${parseFloat(((Number(stoploss) - activeEntry) / activeEntry * 100).toFixed(4))}%`
    : "0%";

  // Long: target% = (target - upperEntry) / upperEntry * 100
  // Short: target% = (lowerEntry - target) / lowerEntry * 100
  const t1Pct = target1 && activeEntry
    ? opinion === "long"
      ? `${parseFloat(((Number(target1) - activeEntry) / activeEntry * 100).toFixed(4))}%`
      : `${parseFloat(((activeEntry - Number(target1)) / activeEntry * 100).toFixed(4))}%`
    : "0%";

  const t2Pct = target2 && activeEntry
    ? opinion === "long"
      ? `${parseFloat(((Number(target2) - activeEntry) / activeEntry * 100).toFixed(4))}%`
      : `${parseFloat(((activeEntry - Number(target2)) / activeEntry * 100).toFixed(4))}%`
    : "0%";

  return (
    <div className="publisher-card">
      <div className="card-header">
        <div className="coin-name">{coin.name}</div>

        <div className="price">
          ${coin.price}
          <span className="green"> ▲{coin.change}</span>
        </div>

        <div className="date">{formatTimestamp(coin.date)}</div>

        <Tooltip
          title="Scalping, defined as a very short-duration trade setup designed to capture small price movements within minutes to a few hours, typically targeting low percentage gains with tight stop-loss levels, is strictly not permitted on CryptoAnts"
          placement="left"
          arrow
        >
          <InfoOutlinedIcon className="card-info-icon" />
        </Tooltip>
      </div>

      <div className="card-divider"></div>


      <div className="form-rows">

        <div className="form-row">
          <div className="form-left form-group">
            <label>My Opinion *</label>
            <div className="toggle">
              <button
                className={opinion === "long" ? "active" : ""}
                onClick={() => setOpinion("long")}
              >
                Long
              </button>
              <button
                className={opinion === "short" ? "active" : ""}
                onClick={() => setOpinion("short")}
              >
                Short
              </button>
            </div>
          </div>
          <div className="form-right form-group" style={{ marginBottom: '10px' }}>
            <label>Mark Price *</label>
            <input
              className="entry-input mark-price-input"
              placeholder="Fetching..."
              value={markPrice}
              onChange={(e) => setMarkPrice(numericOnly(e.target.value))}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-left form-group">
            <label>Validity *</label>
            <div className="date-input-wrap" onClick={() => dateInputRef.current?.showPicker()}>
              {validity
                ? <span className="date-formatted">{formatDateDisplay(validity)}</span>
                : <span className="date-placeholder">Select Date</span>
              }
              <input
                ref={dateInputRef}
                type="date"
                value={validity}
                min={getTodayStr()}
                max={getMaxDateStr()}
                onChange={(e) => setValidity(e.target.value)}
              />
              <CalendarTodayIcon className="date-icon" />
            </div>
            {errors.validity && <div className="error-text">{errors.validity}</div>}
          </div>

          {/* <div className="form-right form-group">
            <label>&nbsp;</label>
            <div className="entry-row">
              <RadioGroup
                row
                value={entryType}
                onChange={(e) => setEntryType(e.target.value)}
                className="entry-radio"
              >
                <FormControlLabel
                  value="limit"
                  control={<Radio size="small" />}
                  label="Limit"
                />
              </RadioGroup>
            </div>
          </div> */}

          <div className="form-right form-group">
            <label>Entry Range *</label>
            <div className="range-row">
              {/* Lower input — primary for Short, disabled for Long until upper is entered */}
              <div className="input-box">
                <span className="input-box__prefix">₹</span>
                <input
                  placeholder="Lower"
                  value={entryRange1}
                  disabled={opinion === "long" && !entryRange2}
                  onChange={(ev) => {
                    const v = numericOnly(ev.target.value);
                    if (opinion === "short") {
                      // primary for short: auto-calc upper
                      setEntryRange1(v);
                      const n = Number(v);
                      setEntryRange2(n ? String(parseFloat((n + n * 0.015).toFixed(4))) : "");
                    } else {
                      // secondary for long: free edit
                      setEntryRange1(v);
                    }
                  }}
                />
              </div>
              {/* Upper input — primary for Long, disabled for Short until lower is entered */}
              <div className="input-box">
                <span className="input-box__prefix">₹</span>
                <input
                  placeholder="Upper"
                  value={entryRange2}
                  disabled={opinion === "short" && !entryRange1}
                  onChange={(ev) => {
                    const v = numericOnly(ev.target.value);
                    if (opinion === "long") {
                      // primary for long: auto-calc lower
                      setEntryRange2(v);
                      const n = Number(v);
                      setEntryRange1(n ? String(parseFloat((n - n * 0.015).toFixed(4))) : "");
                    } else {
                      // secondary for short: free edit
                      setEntryRange2(v);
                    }
                  }}
                />
              </div>
            </div>
            <span className="sub-text">
              {opinion === "long"
                ? entryRange2 ? `Lower auto-filled (±1.5% of upper), editable` : "Enter upper first — lower auto-fills"
                : entryRange1 ? `Upper auto-filled (±1.5% of lower), editable` : "Enter lower first — upper auto-fills"}
            </span>
            {errors.primaryEntry && <div className="error-text">{errors.primaryEntry}</div>}
            {errors.entryRange && <div className="error-text">{errors.entryRange}</div>}
          </div>


        </div>

        <div className="form-row">
          <div className="form-left form-group">
            <label>Leverage *</label>
            <input
              placeholder="0X"
              value={leverage}
              onChange={(e) => setLeverage(numericOnly(e.target.value))}
            />
            {errors.leverage && <div className="error-text">{errors.leverage}</div>}
            <span className="sub-text">
              {coin.backendLeverage
                ? `Range: 3x – ${coin.backendLeverage}x`
                : "Min: 3x"}
            </span>
          </div>
          <div className="form-right form-group">
            <label>Stoploss *</label>
            <div className="input-box">
              <span className="input-box__prefix">₹</span>
              <input
                placeholder="200"
                value={stoploss}
                onChange={(e) => {
                  const v = numericOnly(e.target.value);
                  setStoploss(v);
                  if (!v) setTargetTouched(false);
                }}
              />
              <span className="input-box__suffix">{slPct}</span>
            </div>
            <span className="sub-text">
              {activeEntry && leverage
                ? `Range: ${slRangeMin} – ${slRangeMax}`
                : activeEntry
                  ? opinion === "long"
                    ? `Max: ${slRangeMax} (enter leverage for min)`
                    : `Min: ${slRangeMin} (enter leverage for max)`
                  : "Must be within 0.3% – 20%(leveraged) of Entry"}
            </span>
            {errors.stoploss && <div className="error-text">{errors.stoploss}</div>}
          </div>

        </div>

        <div className="form-row">
          <div className="form-left">&nbsp;</div>
          <div className="form-right  form-group">
            <div className="targets-row">
              <div className="form-group">
                <label>Target 1 *</label>
                <div className="input-box">
                  <span className="input-box__prefix">₹</span>
                  <input
                    placeholder="100"
                    value={target1}
                    onChange={(e) => {
                      if (!stoploss) {
                        setTargetTouched(true);
                        return;
                      }
                      setTarget1(numericOnly(e.target.value));
                    }}
                  />
                  <span className="input-box__suffix">{t1Pct}</span>
                </div>
                <span className="sub-text">
                  RR- 1:1; min. 1.5%{t1Limit !== null ? ` | ${opinion === "long" ? "Min" : "Max"}: ${t1Limit}` : ""}
                </span>
                {!stoploss && targetTouched && <div className="error-text">Please add stoploss first</div>}
                {stoploss && errors.target1 && <div className="error-text">{errors.target1}</div>}
              </div>

              <div className="form-group">
                <label>Target 2 *</label>
                <div className="input-box">
                  <span className="input-box__prefix">₹</span>
                  <input
                    placeholder="90"
                    value={target2}
                    onChange={(e) => {
                      if (!stoploss) {
                        setTargetTouched(true);
                        return;
                      }
                      setTarget2(numericOnly(e.target.value));
                    }}
                  />
                  <span className="input-box__suffix">{t2Pct}</span>
                </div>
                <span className="sub-text">
                  RR-1:1.5; min. 2.5%{t2Limit !== null ? ` | ${opinion === "long" ? "Min" : "Max"}: ${opinion === "short" && target1 && Number(target1) < t2Limit
                    ? parseFloat(Number(target1).toFixed(4))
                    : t2Limit
                    }` : ""}
                </span>
                {!stoploss && targetTouched && <div className="error-text">Please add stoploss first</div>}
                {stoploss && errors.target2 && <div className="error-text">{errors.target2}</div>}
              </div>
            </div>
          </div>
        </div>

      </div>

      {errors.general && (
        <div className="error-text" style={{ textAlign: "center", marginTop: '20px', marginBottom: "10px" }}>
          {errors.general}
        </div>
      )}
      <div className="card-footer">
        <button
          className={`ai-score-btn${isPublished ? " published-btn" : ""}`}
          onClick={isPublished ? undefined : handleLogicClick}
          disabled={isPublished}
        >
          {isPublished ? "Published" : "LOGIC"}
        </button>
      </div>
      <LogicModal
        open={openLogic}
        onClose={() => setOpenLogic(false)}
        onPublishSuccess={() => { setIsPublished(true); setShowSuccess(true); }}
        opinion={opinion}
        validity={validity}
        scripId={coin.id}
        entry={entryMidpoint}
        target1={target1}
        target2={target2}
        stoploss={stoploss}
        leverage={leverage}
        entryRange1={entryRange1}
        entryRange2={entryRange2}
      />
      <AiConfidenceModal
        open={openAiScore}
        onClose={() => setOpenAiScore(false)}
        onAddLogic={() => { setOpenAiScore(false); setOpenLogic(true); }}
        score={coin.confidenceScore}
      />
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)} sx={{ width: "100%" }}>
          Recommendation published successfully!
        </Alert>
      </Snackbar>
    </div>
  );
}

export default function Publisher() {
  const [coinsData, setCoins] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearchResult = (coin) => {
    setCoins((prev) => {
      const filtered = prev.filter((c) => c.name !== coin.name);
      return [coin, ...filtered];
    });
  };

  useEffectOnce(() => {
    fetchCoins();
  }, []);

  const fetchCoins = async () => {
    try {
      setLoading(true);

      const response = await serverAddr.post("/publisher/get_crypto_coins_details/", {
        page_no: 1,
        page_per_entries: 6
      });

      const data = response?.data || [];

      const formatted = data.map((item) => {
        return {
          id: item.id,
          name: item.title || '',
          price: item.price,
          change: item.price_change,
          date: item.timestamp,
          confidenceScore: item.confidence_score || 2,
          markPrice: item.mark_price,
          backendLeverage: item.leverage,
        };
      });

      setCoins(formatted);

    } catch (error) {
      console.error("Error fetching coins:", error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="publisher-wrapper">
      <div className="search-bar">
        <SearchCoin onSelect={handleSearchResult} />
      </div>

      <div className="publisher-container">
        {coinsData.filter((coin) => coin.id).map((coin, index) => (
          <Card key={coin.id} coin={coin} setCoins={setCoins} index={index} />
        ))}
      </div>
    </div>
  );
}
